import API_ERROR_RESPONSES from 'Settings/error-strings';
import { NODE_ENV } from 'Settings';

/*
  Every error on the site receives a standard error object.
  This takes a standard API result and translates it into something
  useful to the app. In addition, this is where the actual
  text strings are selected for user display. The ones in ERROR_STRINGS
  are output to the console for developers.
*/

export const normalizeAPIError = (error, actionType, formName) => {
  // First, let’s see if there is 1:1 match for our error types.
  const errorCode = error.errorCode ? parseInt(error.errorCode) : -1;
  const apiError = API_ERROR_RESPONSES[errorCode];
  logDeveloperMessage(error);

  const msgId = apiError && apiError.msgId ? apiError.msgId : undefined;
  const normalizedError = getNormalizedErrorMessage(msgId, errorCode);
  // This handles any exceptions where the error code doesn’t map
  // directly to a single message.
  switch (errorCode) {
    case 16:
      if (formName === 'changePassword') {
        return {
          ...normalizedError,
          msgId: 'errors.current_password',
        };
      }
      return normalizedError;
    // This handles when a teacher is trying to add a student to a
    // class but the username is taken.
    case 22:
      const usernameMessage = {
        type: 'error',
        errorCode,
        msgId: 'errors.username_exists',
      };
      if (error.errorVals.errors[0][0] === 'student_exists') {
        const usernameUser = error.errorVals.errors[0][1];
        if (formName === 'addStudentToSchool')
          return {
            type: 'info',
            animationType: 'fade',
            msgId: 'errors.username_exists_reconciliation',
            msgValues: {
              given_name: usernameUser.given_name,
              family_name: usernameUser.family_name,
            },
          };
        return {
          ...usernameMessage,
          type: 'reconciliation',
          details: usernameUser,
          msgId: 'errors.username_exists_reconciliation',
          animationType: 'persist',
          msgValues: {
            given_name: usernameUser.given_name,
            family_name: usernameUser.family_name,
          },
        };
      } else {
        return usernameMessage;
      }
    // Expired reset/invite token
    // TODO: Special-case the string based on token type
    case 33:
      return formatErrorMessage(
        actionType.includes('INVITE')
          ? 'errors.expired_invite_token'
          : 'errors.expired_reset_password_token',
        errorCode
      );
    // Invalid reset/invite token
    // TODO: Special-case the string based on token type
    case 35:
    case 60:
      return formatErrorMessage(
        actionType.includes('INVITE')
          ? 'errors.invalid_invite_token'
          : 'errors.invalid_reset_password_token',
        errorCode
      );
    // Already used reset/invite token
    // TODO: Special-case the string based on token type
    case 45:
      return formatErrorMessage(
        actionType.includes('INVITE')
          ? 'errors.used_invite_token'
          : 'errors.used_reset_password_token',
        errorCode
      );
    case 1000:
    case 1001:
      return {
        type: 'error',
        errorCode,
        msgId: 'errors.server_communication',
      };
    default:
      if (normalizedError) return normalizedError;
      return {
        type: 'error',
        errorCode,
        text: error.errorMsg,
        animationType: 'persist',
      };
  }
};

export const getNormalizedErrorMessage = (msgId, errorCode = null) => {
  if (!msgId) return null;
  return formatErrorMessage(msgId, { errorCode });
};

const formatErrorMessage = (msgId, additionalArgs = {}) => {
  return {
    type: 'error',
    msgId,
    animationType: 'persist',
    ...additionalArgs,
  };
};

const logDeveloperMessage = serverError => {
  if (NODE_ENV !== 'test') console.log(`Backend Error Details:`, serverError);
};

/*
  These errors appear underneath a form input if a user submits a form
  and the input field does not meet our requirements.
*/

export const getInputErrorMessageId = (field, value, validity) => {
  // This means we have set an explicit pattern for the field to match.
  // For example, we use it with the password and username fields.
  // In this case, we are showing the requirements in a note, so we pass it through.
  if (validity.patternMismatch) return field.noteId;

  if (validity.customError) return field.stickyErrorMsgId;

  // When the input has a specific type (e.g., email) and the browser
  // determines the value does not match it.
  if (validity.typeMismatch) {
    if (field.type === 'email') return 'form.field.error_text.email';
    return 'form.field.error_text.type_mismatch';
  }

  // If a field is required and the user has not inputted _anything_.
  if (field.required && value.length === 0)
    return 'form.field.error_text.required';

  // If a field is required to have a minimum character count (like usernames).
  // Buuuut, if the field already has a note, we should show that instead
  // since it likely has the rules.
  if (
    field.minLength &&
    (validity.tooShort || value.length < field.minLength)
  ) {
    if (field.noteId) return field.noteId;
    return 'form.field.error_text.too_short';
  }

  // This is just like the minimum length, except most browswers make it
  // impossible to add more than the maximum. So, this is
  // unlikely to be displayed.
  if (field.maxLength && (validity.tooLong || value.length > field.maxLength)) {
    if (field.noteId) return field.noteId;
    return 'form.field.error_text.too_long';
  }

  // Finally, if we have a checkbox field (like accepting our terms)
  // that the user must set to true, we show this error.
  if (field.type === 'checkbox' && field.requiredTrue && !value) {
    return 'form.field.error_text.required_checkbox';
  }
};

export const getInputErrorMessageValues = field => {
  if (field.stickyErrorMsgValues !== undefined)
    return field.stickyErrorMsgValues;
  switch (field.errorMsgId) {
    case 'form.field.error_text.type_mismatch':
      return { field_type: field.type };
    case 'form.field.error_text.too_short':
      return { field_min_length: field.minLength };
    case 'form.field.error_text.too_long':
      return { field_max_length: field.maxLength };
    default:
      return undefined;
  }
};
